/**
 * Diccionario interno de traducciones (fallback) para MasterPatternView.
 *
 * Usado por `useMasterPatternViewTranslation` como último nivel de la cascada de
 * resolución de i18n, cuando ni `t` (prop), ni `externalTranslations`, ni el
 * sistema global de traducción (`LanguageManagement`) resuelven la llave.
 *
 * @module i18n/internalTranslations
 */
export const internalTranslations: Record<string, Record<string, string>> = {
  es: {
    // ── Genéricos / reutilizados en múltiples componentes ──────────────────
    save: 'Guardar',
    saving: 'Guardando…',
    cancel: 'Cancelar',
    create: 'Nuevo',
    edit: 'Editar',
    delete: 'Eliminar',
    confirm: 'Confirmar',
    back: 'Regresar',
    search: 'Buscar',
    clear: 'Limpiar',
    import: 'Importar',
    export: 'Exportar',
    actions: 'Acciones',
    active: 'Activo',
    inactive: 'Inactivo',
    activateAction: 'Activar',
    deactivateAction: 'Desactivar',
    activateVerb: 'activar',
    deactivateVerb: 'desactivar',
    enableRecordAction: 'Activar registro',
    disableRecordAction: 'Desactivar registro',
    discardChanges: 'Cancelar',
    saveChanges: 'Guardar',
    global: 'Global',
    yes: 'Sí',
    no: 'No',
    all: 'Todos',
    // "enable"/"disable" son específicamente para "Habilitar por compañía" (acceso
    // multi-compañía a un registro GLOBAL) — un concepto distinto al estado
    // activo/inactivo del registro (ese usa activateAction/deactivateAction y
    // enableRecordAction/disableRecordAction, que sí dicen "Activar"/"Desactivar").
    enable: 'Habilitar',
    disable: 'Deshabilitar',
    readOnly: 'Solo lectura',
    loading: 'Cargando…',
    changeStatus: 'Cambiar estado',
    close: 'Cerrar',
    detail: 'Detalle',
    moreActions: 'Más acciones',
    auditChanges: 'Auditoría de Cambios',
    auditHistory: 'Historial de cambios',

    // ── MasterPatternView.tsx (toolbar / acciones / modales inline) ────────
    toolbarSearchLabel: 'Buscar...',
    showAdvancedSearch: 'Mostrar filtros',
    hideAdvancedSearch: 'Ocultar filtros',
    recordsToShow: 'Registros a mostrar',
    viewCompanyOnly: 'Ver solo registros de la compañía seleccionada',
    showGlobalValuesLabel: 'Mostrar datos globales',
    enableCompaniesAction: 'Habilitar compañías',
    loadingMore: 'Cargando más...',
    companyChangesSavedSuccess: 'Cambios de compañías guardados correctamente',
    companyChangesSavedError: 'Error al guardar los cambios de compañías',
    unsavedChangesBlockSwitch: 'Tienes cambios sin guardar. Guarda o cancela los cambios antes de cambiar de compañía.',
    recordNotEnabledForCompany: 'El registro no está habilitado para {company}. Habilítalo desde la lista antes de editar en ese contexto.',
    recordEnabledSuccess: 'Registro habilitado correctamente',
    recordEnabledError: 'Error al habilitar el registro',
    changeViewAriaLabel: 'Cambiar vista',
    tableViewTitle: 'Vista tabla',
    cardsViewTitle: 'Vista cards',
    displayMode: 'Modo de visualización',
    listView: 'Listado',
    cardView: 'Tarjetas',
    moreOptions: 'Más opciones',

    // ── DirtyStateDialog ─────────────────────────────────────────────────────
    dirtyDialogTitle: 'Tienes cambios sin guardar',
    dirtyDialogDesc: 'Si sales ahora perderás los siguientes cambios. ¿Deseas guardarlos antes de volver al listado?',
    dirtyDialogContinue: 'Seguir editando',
    dirtyDialogDiscard: 'Descartar y salir',
    dirtyDialogSaveExit: 'Guardar y salir',
    modifiedFields: 'Campos modificados',
    emptyValue: 'vacío',

    // ── ChangeStatusModal ────────────────────────────────────────────────────
    changeStatusConfirmTemplate: '¿Desea {action} este registro?',
    changeStatusScopeNotePrefix: 'Solo se modificará el estado para ',
    confirmDeactivateTitle: '¿Estás seguro de desactivar este registro?',
    confirmActivateTitle: '¿Estás seguro de activar este registro?',
    confirmDeactivateDesc: 'El registro dejará de estar disponible.',
    confirmActivateDesc: 'El registro volverá a estar disponible.',

    // ── NotEnabledModal ──────────────────────────────────────────────────────
    notEnabledTitle: 'Registro no habilitado',
    notEnabledActionEdit: '¿Desea habilitarlo y continuar con la edición?',
    notEnabledActionChangeStatus: '¿Desea habilitarlo y cambiar su estado?',
    notEnabledActionView: '¿Desea habilitarlo y continuar con la visualización?',
    notEnabledActionCompanySwitch: '¿Desea habilitarlo para continuar con la edición en esta compañía?',
    notEnabledConfirmContinue: 'Habilitar y continuar',
    notEnabledConfirmChangeStatus: 'Habilitar y cambiar estado',
    notEnabledBodyPrefix: 'Este registro no está habilitado en ',
    notEnabledWarning: 'Los valores globales se conservarán hasta que se personalicen los campos.',
    notEnabledLoadingLabel: 'Habilitando…',

    // ── CompanyEnableModal ───────────────────────────────────────────────────
    companyEnableTitle: 'Habilitar por compañía',
    companyEnableDesc: 'Gestiona en qué compañías está habilitado este registro.',
    companySearchPlaceholder: 'Buscar compañía…',
    enableAllCompanies: 'Habilitar todas',
    noCompaniesFound: 'No se encontraron compañías',
    confirmChanges: 'Confirmar cambios',
    confirmChangesCount: 'Confirmar cambios ({count})',

    // ── ConflictDialog ───────────────────────────────────────────────────────
    conflictTitle: 'Conflicto de versión',
    conflictSubtitle: 'El registro fue modificado por otro usuario',
    conflictBody: 'Este registro fue modificado por otro usuario mientras lo editabas. Recarga los datos para ver la versión más reciente antes de continuar.',
    conflictRefreshButton: 'Refrescar datos',

    // ── EmptyState ───────────────────────────────────────────────────────────
    emptyNoResultsTitle: 'Sin resultados',
    emptyEnterCriteriaTitle: 'Ingresa criterios y presiona Buscar',
    emptyNoResultsDesc: 'No hay registros que coincidan con los filtros.',
    emptyEnterCriteriaDesc: 'Usa los filtros del panel superior para encontrar registros.',
    emptyEnterCriteriaAlt: 'Ingresa criterios',

    // ── FormActionBar ────────────────────────────────────────────────────────
    useGlobalData: 'Usar datos globales',
    enableCompanyAction: 'Habilitar compañía',
    createNewRecord: 'Crear nuevo registro',
    editUnavailable: 'Editar no disponible',

    // ── DetailTab ────────────────────────────────────────────────────────────
    addRecordPrefix: 'Agregar ',
    editRecordPrefix: 'Editar ',
    deleteConfirmQuestion: '¿Eliminar este registro?',
    detailEmptyMessage: 'Sin registros. Agrega el primero.',

    // ── TabBar ───────────────────────────────────────────────────────────────
    basicDataTabLabel: 'Datos básicos',

    // ── FilterPanel ──────────────────────────────────────────────────────────
    opContains: 'contiene',
    opStartsWith: 'empieza con',
    opEquals: 'es igual a',
    opNotEquals: 'no es igual a',
    opGt: 'mayor que',
    opGte: 'mayor o igual a',
    opLt: 'menor que',
    opLte: 'menor o igual a',
    opFrom: 'desde',
    opTo: 'hasta',
    statusLabel: 'Estado',
    advancedFilters: 'Filtros avanzados',
    advancedFiltersHint: '— combina varias condiciones personalizadas',
    connectorLabel: 'Conector:',
    joinConditionsWith: 'Unir condiciones con',
    connectorAndLabel: 'Y',
    connectorOrLabel: 'O',
    connectorAndHint: 'Se deben cumplir todas las condiciones',
    connectorOrHint: 'Basta con que se cumpla una condición',
    column: 'Columna',
    condition: 'Condición',
    value: 'Valor',
    selectPlaceholder: 'Seleccionar...',
    valuePlaceholder: 'Valor...',
    removeConditionTitle: 'Eliminar condición',
    addConditionLabel: 'Agregar condición',
    activeFiltersLabel: 'Filtros activos:',
    clearAdvancedFilters: 'Limpiar filtros avanzados',

    // ── DateRangeFilterControl ───────────────────────────────────────────────
    dateFromPlaceholder: 'Desde (YYYY-MM-DD)',
    dateToPlaceholder: 'Hasta (YYYY-MM-DD)',

    // ── NumberFilterControl ──────────────────────────────────────────────────
    opEqSymbol: '=',
    opNeqSymbol: '≠',
    opGtSymbol: '>',
    opLtSymbol: '<',
    opBetween: 'Entre',
    toPlaceholder: 'Hasta',

    // ── TextFilterControl ────────────────────────────────────────────────────
    opContainsCap: 'Contiene',
    opStartsWithCap: 'Empieza con',
    opEqualToCap: 'Igual a',

    // ── RowActions ───────────────────────────────────────────────────────────
    viewAction: 'Visualizar',

    // ── PaginationBar ────────────────────────────────────────────────────────
    paginationPrev: 'Anterior',
    paginationNext: 'Siguiente',
    paginationOf: 'de',
    paginationRecords: 'registros',
    paginationPerPage: 'por página',

    // ── BatchBar ─────────────────────────────────────────────────────────────
    recordWord: 'registro',
    recordsPlural: 'registros',
    selectedSingular: 'seleccionado',
    selectedPlural: 'seleccionados',
    clearSelectionAriaLabel: 'Limpiar selección',
    batchChangeStatus: 'Cambiar estado masivo',
    batchNoPermissionChangeStatus: 'No tienes permisos para cambiar el estado',
    batchActionError: 'Error al ejecutar la acción',

    // ── CompanySelector ──────────────────────────────────────────────────────
    noCompaniesConfigured: 'No hay compañías configuradas para este maestro.',

    // ── useMasterForm ────────────────────────────────────────────────────────
    validationRequired: 'Este campo es requerido',
    validationNumber: 'Debe ser un número válido',
    validationDateFormat: 'Formato de fecha inválido (YYYY-MM-DD)',
    validationMaxLength: 'Excede la longitud máxima permitida',
    createEnabledSuccess: 'Registro creado y habilitado para la compañía exitosamente',
    createEnabledPartialError: 'Registro creado, pero no se pudo habilitar automáticamente. Habilítelo desde la lista.',
    createSuccess: 'Registro creado exitosamente',
    createError: 'Error al crear el registro',
    updateSuccess: 'Registro actualizado exitosamente',
    updateError: 'Error al actualizar el registro',

    // ── useMasterList ────────────────────────────────────────────────────────
    loadRecordsError: 'Error al cargar los registros',

    // ── useChangeStatus ──────────────────────────────────────────────────────
    statusChangeSuccess: 'Estado cambiado exitosamente',
    statusChangeError: 'Error al cambiar el estado',

    // ── MasterPatternViewForm ────────────────────────────────────────────────
    recordLoadFailedError: 'No se pudo cargar la información completa del registro.',
    retryButton: 'Reintentar',

    // ── ListTable / ListCard (badges, tooltips, column filters) ──────────────
    companyAssignedTooltip: 'Habilitado para esta compañía',
    companyOverrideTooltip: 'Valor sobreescrito por la compañía',
    companyGlobalValueTooltip: 'Valor global',
    companyStatus: 'Habilitación en la compañía seleccionada',
    enabledInCompany: 'Habilitado en la compañía seleccionada',
    notEnabledInCompany: 'No habilitado en la compañía seleccionada',
    filterPlaceholder: 'Filtrar...',
  },
  en: {
    // ── Generic / shared across multiple components ─────────────────────────
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    confirm: 'Confirm',
    back: 'Back',
    search: 'Search',
    clear: 'Clear',
    import: 'Import',
    export: 'Export',
    actions: 'Actions',
    active: 'Active',
    inactive: 'Inactive',
    activateAction: 'Activate',
    deactivateAction: 'Deactivate',
    activateVerb: 'activate',
    deactivateVerb: 'deactivate',
    enableRecordAction: 'Enable record',
    disableRecordAction: 'Disable record',
    discardChanges: 'Cancel',
    saveChanges: 'Save',
    global: 'Global',
    yes: 'Yes',
    no: 'No',
    all: 'All',
    enable: 'Enable',
    disable: 'Disable',
    readOnly: 'Read only',
    loading: 'Loading…',
    changeStatus: 'Change status',
    close: 'Close',
    detail: 'Detail',
    moreActions: 'More actions',
    auditChanges: 'Change audit',
    auditHistory: 'Change history',

    // ── MasterPatternView.tsx (toolbar / actions / inline modals) ──────────
    toolbarSearchLabel: 'Search...',
    showAdvancedSearch: 'Show filters',
    hideAdvancedSearch: 'Hide filters',
    recordsToShow: 'Records to show',
    viewCompanyOnly: 'View only records from the selected company',
    showGlobalValuesLabel: 'Show global data',
    enableCompaniesAction: 'Enable companies',
    loadingMore: 'Loading more...',
    companyChangesSavedSuccess: 'Company changes saved successfully',
    companyChangesSavedError: 'Error saving company changes',
    unsavedChangesBlockSwitch: 'You have unsaved changes. Save or cancel the changes before switching companies.',
    recordNotEnabledForCompany: 'The record is not enabled for {company}. Enable it from the list before editing in that context.',
    recordEnabledSuccess: 'Record enabled successfully',
    recordEnabledError: 'Error enabling the record',
    changeViewAriaLabel: 'Change view',
    tableViewTitle: 'Table view',
    cardsViewTitle: 'Card view',
    displayMode: 'Display mode',
    listView: 'List',
    cardView: 'Cards',
    moreOptions: 'More options',

    // ── DirtyStateDialog ─────────────────────────────────────────────────────
    dirtyDialogTitle: 'You have unsaved changes',
    dirtyDialogDesc: 'If you leave now you will lose the following changes. Do you want to save them before returning to the list?',
    dirtyDialogContinue: 'Keep editing',
    dirtyDialogDiscard: 'Discard and exit',
    dirtyDialogSaveExit: 'Save and exit',
    modifiedFields: 'Modified fields',
    emptyValue: 'empty',

    // ── ChangeStatusModal ────────────────────────────────────────────────────
    changeStatusConfirmTemplate: 'Do you want to {action} this record?',
    changeStatusScopeNotePrefix: 'Only the status will be changed for ',
    confirmDeactivateTitle: 'Are you sure you want to deactivate this record?',
    confirmActivateTitle: 'Are you sure you want to activate this record?',
    confirmDeactivateDesc: 'The record will no longer be available.',
    confirmActivateDesc: 'The record will be available again.',

    // ── NotEnabledModal ──────────────────────────────────────────────────────
    notEnabledTitle: 'Record not enabled',
    notEnabledActionEdit: 'Do you want to enable it and continue editing?',
    notEnabledActionChangeStatus: 'Do you want to enable it and change its status?',
    notEnabledActionView: 'Do you want to enable it and continue viewing?',
    notEnabledActionCompanySwitch: 'Do you want to enable it to continue editing in this company?',
    notEnabledConfirmContinue: 'Enable and continue',
    notEnabledConfirmChangeStatus: 'Enable and change status',
    notEnabledBodyPrefix: 'This record is not enabled in ',
    notEnabledWarning: 'Global values will be kept until fields are customized.',
    notEnabledLoadingLabel: 'Enabling…',

    // ── CompanyEnableModal ───────────────────────────────────────────────────
    companyEnableTitle: 'Enable by company',
    companyEnableDesc: 'Manage which companies this record is enabled for.',
    companySearchPlaceholder: 'Search company…',
    enableAllCompanies: 'Enable all',
    noCompaniesFound: 'No companies found',
    confirmChanges: 'Confirm changes',
    confirmChangesCount: 'Confirm changes ({count})',

    // ── ConflictDialog ───────────────────────────────────────────────────────
    conflictTitle: 'Version conflict',
    conflictSubtitle: 'The record was modified by another user',
    conflictBody: 'This record was modified by another user while you were editing it. Reload the data to see the latest version before continuing.',
    conflictRefreshButton: 'Refresh data',

    // ── EmptyState ───────────────────────────────────────────────────────────
    emptyNoResultsTitle: 'No results',
    emptyEnterCriteriaTitle: 'Enter criteria and press Search',
    emptyNoResultsDesc: 'There are no records matching the filters.',
    emptyEnterCriteriaDesc: 'Use the filters in the top panel to find records.',
    emptyEnterCriteriaAlt: 'Enter criteria',

    // ── FormActionBar ────────────────────────────────────────────────────────
    useGlobalData: 'Use global data',
    enableCompanyAction: 'Enable company',
    createNewRecord: 'Create new record',
    editUnavailable: 'Edit unavailable',

    // ── DetailTab ────────────────────────────────────────────────────────────
    addRecordPrefix: 'Add ',
    editRecordPrefix: 'Edit ',
    deleteConfirmQuestion: 'Delete this record?',
    detailEmptyMessage: 'No records. Add the first one.',

    // ── TabBar ───────────────────────────────────────────────────────────────
    basicDataTabLabel: 'Basic data',

    // ── FilterPanel ──────────────────────────────────────────────────────────
    opContains: 'contains',
    opStartsWith: 'starts with',
    opEquals: 'is equal to',
    opNotEquals: 'is not equal to',
    opGt: 'greater than',
    opGte: 'greater than or equal to',
    opLt: 'less than',
    opLte: 'less than or equal to',
    opFrom: 'from',
    opTo: 'to',
    statusLabel: 'Status',
    advancedFilters: 'Advanced filters',
    advancedFiltersHint: '— combine several custom conditions',
    connectorLabel: 'Connector:',
    joinConditionsWith: 'Join conditions with',
    connectorAndLabel: 'And',
    connectorOrLabel: 'Or',
    connectorAndHint: 'All conditions must be met',
    connectorOrHint: 'It is enough for one condition to be met',
    column: 'Column',
    condition: 'Condition',
    value: 'Value',
    selectPlaceholder: 'Select...',
    valuePlaceholder: 'Value...',
    removeConditionTitle: 'Remove condition',
    addConditionLabel: 'Add condition',
    activeFiltersLabel: 'Active filters:',
    clearAdvancedFilters: 'Clear advanced filters',

    // ── DateRangeFilterControl ───────────────────────────────────────────────
    dateFromPlaceholder: 'From (YYYY-MM-DD)',
    dateToPlaceholder: 'To (YYYY-MM-DD)',

    // ── NumberFilterControl ──────────────────────────────────────────────────
    opEqSymbol: '=',
    opNeqSymbol: '≠',
    opGtSymbol: '>',
    opLtSymbol: '<',
    opBetween: 'Between',
    toPlaceholder: 'To',

    // ── TextFilterControl ────────────────────────────────────────────────────
    opContainsCap: 'Contains',
    opStartsWithCap: 'Starts with',
    opEqualToCap: 'Equal to',

    // ── RowActions ───────────────────────────────────────────────────────────
    viewAction: 'View',

    // ── PaginationBar ────────────────────────────────────────────────────────
    paginationPrev: 'Previous',
    paginationNext: 'Next',
    paginationOf: 'of',
    paginationRecords: 'records',
    paginationPerPage: 'per page',

    // ── BatchBar ─────────────────────────────────────────────────────────────
    recordWord: 'record',
    recordsPlural: 'records',
    selectedSingular: 'selected',
    selectedPlural: 'selected',
    clearSelectionAriaLabel: 'Clear selection',
    batchChangeStatus: 'Bulk change status',
    batchNoPermissionChangeStatus: 'You do not have permission to change the status',
    batchActionError: 'Error executing the action',

    // ── CompanySelector ──────────────────────────────────────────────────────
    noCompaniesConfigured: 'No companies configured for this master.',

    // ── useMasterForm ────────────────────────────────────────────────────────
    validationRequired: 'This field is required',
    validationNumber: 'Must be a valid number',
    validationDateFormat: 'Invalid date format (YYYY-MM-DD)',
    validationMaxLength: 'Exceeds the maximum allowed length',
    createEnabledSuccess: 'Record created and enabled for the company successfully',
    createEnabledPartialError: 'Record created, but it could not be enabled automatically. Enable it from the list.',
    createSuccess: 'Record created successfully',
    createError: 'Error creating the record',
    updateSuccess: 'Record updated successfully',
    updateError: 'Error updating the record',

    // ── useMasterList ────────────────────────────────────────────────────────
    loadRecordsError: 'Error loading records',

    // ── useChangeStatus ──────────────────────────────────────────────────────
    statusChangeSuccess: 'Status changed successfully',
    statusChangeError: 'Error changing the status',

    // ── MasterPatternViewForm ────────────────────────────────────────────────
    recordLoadFailedError: 'The complete record information could not be loaded.',
    retryButton: 'Retry',

    // ── ListTable / ListCard (badges, tooltips, column filters) ──────────────
    companyAssignedTooltip: 'Enabled for this company',
    companyOverrideTooltip: 'Value overridden by the company',
    companyGlobalValueTooltip: 'Global value',
    companyStatus: 'Enablement in the selected company',
    enabledInCompany: 'Enabled in the selected company',
    notEnabledInCompany: 'Not enabled in the selected company',
    filterPlaceholder: 'Filter...',
  },
};
